import {
  isFileDeleted,
  isCsvFile,
  isExcelFile,
  shallowEqualArrays,
  deepEqualExclusionConditions,
  hasExclusionConflictWithPrimaryKey,
  hasHashColumnConflictWithPrimaryKey,
  validateFileFormat,
  validateFileName,
  validateFileSize,
  validateHeaderConsistency,
  validateSheetName,
  validateEmptyFile,
  getUniqueValuesForExclusion,
  createFileStore,
  getExcelSheetList
} from './helpers'
import { FILE_STATE } from './types'
import { MAX_FILE_SIZE, formatFileSizeLimit } from './constants'

/** 除外条件で対象カラムのみ選択され値が未選択のときのエラーメッセージ */
export const EXCLUSION_VALUE_REQUIRED_MESSAGE = '除外する値を1件以上選択してください'

/**
 * 除外条件の対象カラムは選択済みだが除外値が未選択の条件を fileKey ごとに返す
 *
 * @param {import('./types').FileState[]} files
 * @returns {Record<string, number[]>}
 */
export const findExclusionValueValidationErrorsByFileKey = (files) => {
  /** @type {Record<string, number[]>} */
  const byFileKey = {}
  for (const file of files) {
    if (isFileDeleted(file.fileState)) continue
    ;(file.exclusionConditions || []).forEach((cond, index) => {
      if (cond.column && (!cond.values || cond.values.length === 0)) {
        if (!byFileKey[file.fileKey]) byFileKey[file.fileKey] = []
        byFileKey[file.fileKey].push(index)
      }
    })
  }
  return byFileKey
}

/**
 * UTF-8 文字列の SHA-256 16進ダイジェスト（バックエンド sha256_hex と同形式）
 *
 * @param {string} message
 * @returns {Promise<string>}
 */
const sha256Hex = async (message) => {
  const data = new TextEncoder().encode(message)
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** SHA-256 を並列実行する際のチャンクサイズ（全件同時だとメモリを圧迫するため分割する） */
const SHA256_CHUNK_SIZE = 2000

/**
 * 文字列配列をまとめて SHA-256 する（チャンクごとに並列実行）
 *
 * 1件ずつ await すると次の計算が待たされるため、チャンク単位で Promise.all する。 入力順と出力順は一致する。
 *
 * @param {string[]} messages
 * @returns {Promise<string[]>}
 */
const sha256HexAll = async (messages) => {
  const results = []
  for (let i = 0; i < messages.length; i += SHA256_CHUNK_SIZE) {
    const chunk = messages.slice(i, i + SHA256_CHUNK_SIZE)
    const hashed = await Promise.all(chunk.map((message) => sha256Hex(message)))
    results.push(...hashed)
  }
  return results
}

/**
 * 変換表キーを trim し、空なら null にする（バックエンド normalize_conversion_table_key と同形式）
 *
 * @param {unknown} convKey
 * @returns {string | null}
 */
const normalizeConversionTableKey = (convKey) => {
  if (convKey === undefined || convKey === null) {
    return null
  }
  const trimmed = String(convKey).trim()
  if (trimmed === '') {
    return null
  }
  return trimmed
}

/**
 * @param {any} res
 * @returns {any[] | null}
 */
const conversionValueListFromResponse = (res) => {
  if (Array.isArray(res?.conversion_value_list)) {
    return res.conversion_value_list
  }
  if (Array.isArray(res?.body?.conversion_value_list)) {
    return res.body.conversion_value_list
  }
  return null
}

/**
 * 変換表項目を取得してリスト化する（取得失敗時は null）
 *
 * S3取得は複数回の通信を伴い失敗しうるため、例外を呼び出し元へ伝播させず null に変換する。 呼び出し元は取得失敗と形式不正を同じ「取得できませんでした」として扱う。
 *
 * @param {function(string, string): Promise<any>} fetchConversionValuesFn
 * @param {string} corporationNumber
 * @param {string} conversionTableKey
 * @returns {Promise<any[] | null>}
 */
const fetchConversionValueList = async (fetchConversionValuesFn, corporationNumber, conversionTableKey) => {
  try {
    return conversionValueListFromResponse(await fetchConversionValuesFn(corporationNumber, conversionTableKey))
  } catch (error) {
    console.error('fetchConversionValueList', error)
    return null
  }
}

/**
 * @param {any[]} list
 * @returns {Map<string, string>}
 */
const plainMappingFromConversionList = (list) => {
  const map = new Map()
  for (const item of list) {
    const valueBefore = item.value_before
    if (valueBefore === undefined || valueBefore === null) {
      continue
    }
    const trimmedBefore = String(valueBefore).trim()
    if (trimmedBefore === '') {
      continue
    }
    const valueAfter = item.value_after
    if (valueAfter === undefined || valueAfter === null) {
      continue
    }
    const trimmedAfter = String(valueAfter).trim()
    if (trimmedAfter === '') {
      continue
    }
    map.set(trimmedBefore, trimmedAfter)
  }
  return map
}

/**
 * 変換表の value_before を SHA-256 したキー集合（apply 時のカバレッジ判定用）
 *
 * @param {any[]} list
 * @returns {Promise<Set<string>>}
 */
const buildHashSpaceBeforeKeySet = async (list) => {
  const targets = []
  for (const item of list) {
    const valueBefore = item.value_before
    if (valueBefore === undefined || valueBefore === null) {
      continue
    }
    const trimmed = String(valueBefore).trim()
    if (trimmed === '') {
      continue
    }
    targets.push(trimmed)
  }
  return new Set(await sha256HexAll(targets))
}

/**
 * 変換表 A→B の switch 操作で変換可能な SHA256(A.after) キー集合 （バックエンド build_switch_hash_space_map のキーと同義）
 *
 * @param {Map<string, string>} oldPlain
 * @param {Map<string, string>} newPlain
 * @returns {Promise<Set<string>>}
 */
const buildSwitchCoverageKeySet = async (oldPlain, newPlain) => {
  const targets = []
  for (const [beforePlain, afterA] of oldPlain) {
    if (!newPlain.has(beforePlain)) {
      continue
    }
    const afterB = newPlain.get(beforePlain)
    const trimmedAfterA = String(afterA).trim()
    const trimmedAfterB = String(afterB).trim()
    if (trimmedAfterA === '' || trimmedAfterB === '') {
      continue
    }
    targets.push(trimmedAfterA)
  }
  return new Set(await sha256HexAll(targets))
}

/**
 * アップロード前の基本バリデーション（形式・サイズ・ファイル名）
 *
 * @param {File} file
 * @param {Record<string, import('./types').FileState>} fileList
 * @param {function(string): void} errorHandler
 * @returns {boolean}
 */
export const validateNewFileForUpload = (file, fileList, errorHandler) => {
  if (!validateFileFormat(file)) {
    errorHandler(
      `サポートされていないファイル形式です: ${file.name}\nCSVファイル（.csv）またはExcelファイル（.xlsx、.xls）をアップロードしてください。`
    )
    return false
  }
  const fileNameValidation = validateFileName(file.name)
  if (!fileNameValidation.valid) {
    errorHandler(
      fileNameValidation.error || 'OSのファイル名制限に従う（Windowsは255文字、禁止文字 / \\ : * ? " < > |）'
    )
    return false
  }
  if (!validateFileSize(file, fileList)) {
    errorHandler(`ファイルサイズが上限（${formatFileSizeLimit(MAX_FILE_SIZE)}）を超えています`)
    return false
  }
  return true
}

/**
 * パース後のバリデーション（ヘッダー・シート・空ファイル）
 *
 * @param {import('./types').ParsedFileData} parsedData
 * @param {File} file
 * @param {function(string): void} errorHandler
 * @returns {boolean}
 */
export const validateParsedDataForUpload = (parsedData, file, errorHandler) => {
  const records = Array.isArray(parsedData.records) ? parsedData.records : []
  if (!validateHeaderConsistency(parsedData.headers || [], records)) {
    errorHandler(`${file.name}: ファイルのヘッダーリストがファイルデータのヘッダーリストと一致していません`)
    return false
  }
  const sheetName = parsedData.sheetName || (isCsvFile(file) ? '0' : '')
  const sheetList = parsedData.sheetList || []
  const isExcel = isExcelFile(file)
  if (!validateSheetName(sheetName, sheetList, isExcel)) {
    errorHandler(
      `${file.name}: 選択されたシート名が存在しません（Excelファイルの場合）またはシート名が"0"ではありません（CSVファイルの場合）`
    )
    return false
  }
  if (isExcel && !validateEmptyFile(sheetList, parsedData.headers || [])) {
    errorHandler(`${file.name}: 空のExcelファイルはアップロードできません`)
    return false
  }
  return true
}

/**
 * ファイル差し替え時：新しいファイルのヘッダーが元のファイルのヘッダーと同一であることを検証する
 *
 * @param {string[]} originalHeaders - 元のファイルのヘッダー
 * @param {string[]} newHeaders - 新しいファイルのヘッダー
 * @param {string} newFileName - 新しいファイル名（エラーメッセージ用）
 * @param {function(string): void} errorHandler
 * @returns {boolean}
 */
export const validateFileChangeHeaders = (originalHeaders, newHeaders, newFileName, errorHandler) => {
  if (!shallowEqualArrays(originalHeaders || [], newHeaders || [])) {
    errorHandler(
      `ファイル「${newFileName}」のヘッダー（列名）が元のファイルと一致していません。ファイルを差し替える場合は、同じ列構成のファイルを指定してください。`
    )
    return false
  }
  return true
}

/**
 * ファイル差し替え時：新しいExcelファイルに元のシート名が存在することを検証する
 *
 * @param {string} originalSheetName - 元のファイルのシート名
 * @param {File} newFile - 新しいファイル
 * @param {string} newFileName - 新しいファイル名（エラーメッセージ用）
 * @param {function(string): void} errorHandler
 * @returns {Promise<boolean>}
 */
export const validateFileChangeSheetName = async (originalSheetName, newFile, newFileName, errorHandler) => {
  if (!isExcelFile(newFile)) return true

  const targetSheetName = originalSheetName || ''
  if (!targetSheetName || targetSheetName === '0') return true

  const sheetList = await getExcelSheetList(newFile)
  if (!sheetList.includes(targetSheetName)) {
    errorHandler(
      `ファイル「${newFileName}」にシート「${targetSheetName}」が存在しません。同じシート名を含むファイルを指定してください。`
    )
    return false
  }
  return true
}

/**
 * シート変更時にヘッダー整合性を検証する基準ヘッダーを返す。 未登録ファイル（savedUploadState にもサーバー登録済みステータスにも該当しない）の場合は null を返し、検証をスキップする。
 *
 * @param {string} fileKey
 * @param {import('./types').FileState} file
 * @param {Record<string, import('./types').FileState> | null | undefined} savedUploadState
 * @returns {string[] | null}
 */
export const getRegisteredHeaderBaselineForSheetChange = (fileKey, file, savedUploadState) => {
  const savedRow = savedUploadState?.[fileKey]
  const isServerRegisteredBaseline =
    file.fileState === FILE_STATE.UPLOADED || file.fileState === FILE_STATE.VALIDATION_ERROR
  if (!savedRow && !isServerRegisteredBaseline) {
    return null
  }
  const savedHeaders = savedRow?.headers
  if (Array.isArray(savedHeaders) && savedHeaders.length > 0) {
    return savedHeaders
  }
  return file.headers ?? []
}

/**
 * 登録時のバリデーションエラーを収集する
 *
 * @param {import('./types').FileState[]} changedFiles - 変更があったファイル
 * @param {Record<string, import('./types').FileState>} fileList - 全ファイルリスト
 * @param {Record<string, import('./types').FileState>} savedUploadState - 保存済みの状態
 * @returns {string[]} エラーメッセージの配列
 */
export const collectRegisterValidationErrors = (changedFiles, fileList, savedUploadState) => {
  const errors = []
  const activeFiles = Object.values(fileList).filter((f) => !isFileDeleted(f.fileState))
  const fileCount = activeFiles.length

  for (const file of changedFiles) {
    if (isFileDeleted(file.fileState)) continue

    // キー項目・ファイル種別はセットで扱う（両方未選択、または両方選択済みである必要がある）
    const hasClassification = !!file.fileClassification
    const hasPrimaryKey = !!file.primaryKeyColumn
    const isBaseFile = !!file.isBaseFile

    // ベースファイルは結合キーとしてキー項目が必須。
    // 種別・キー項目が未選択でもベースONにできてしまうため、登録時に検証する。
    // （キー項目未設定のベースファイルは後続処理で「予期せぬエラー」になる）
    if (isBaseFile && !hasPrimaryKey) {
      errors.push(`「${file.fileName}」をベースファイルとして登録する場合、キー項目の選択は必須です。`)
    } else if (hasClassification !== hasPrimaryKey) {
      errors.push(`ファイル「${file.fileName}」のキー項目は必須です。`)
    }

    // 仕様 9.6: ファイル種別選択時はデータ種別も必須
    if (hasClassification && !file.dataCategory) {
      errors.push(`ファイル「${file.fileName}」のデータ種別は必須です。`)
    }

    if (hasExclusionConflictWithPrimaryKey(file)) {
      errors.push(
        `ファイル「${file.fileName}」のキー項目「${file.primaryKeyColumn}」はデータ除外対象に指定できません。`
      )
    }

    // キー項目はハッシュ化対象列に指定できない（画面はキー項目変更時にチェックを外すため通常は発生しないが、防御的に検証する）
    if (hasHashColumnConflictWithPrimaryKey(file)) {
      errors.push(
        `ファイル「${file.fileName}」のキー項目「${file.primaryKeyColumn}」はハッシュ化対象列に指定できません。`
      )
    }

    // 仕様 9.6: virus-check-error / uploaded で isUploadedLocally が false のとき、ファイル名・ヘッダー一致なら「変更されていません」エラー
    // ただし設定（ファイル種別・キー項目・除外条件等）のみ変更した場合は登録を許可する
    const isUnchangedCheckState =
      file.fileState === FILE_STATE.VIRUS_CHECK_ERROR || file.fileState === FILE_STATE.UPLOADED
    if (isUnchangedCheckState && file.isUploadedLocally === false) {
      const savedFile = savedUploadState?.[file.fileKey]
      if (savedFile) {
        const contentUnchanged =
          file.fileName === savedFile.fileName && shallowEqualArrays(file.headers, savedFile.headers)
        if (contentUnchanged) {
          const isSettingChanged =
            file.fileClassification !== savedFile.fileClassification ||
            file.primaryKeyColumn !== savedFile.primaryKeyColumn ||
            String(file.dataCategory ?? '') !== String(savedFile.dataCategory ?? '') ||
            Boolean(file.isBaseFile) !== Boolean(savedFile.isBaseFile) ||
            file.idConversionTable !== savedFile.idConversionTable ||
            !deepEqualExclusionConditions(file.exclusionConditions || [], savedFile.exclusionConditions || []) ||
            !shallowEqualArrays(file.hashColumns || [], savedFile.hashColumns || []) ||
            file.sheetName !== savedFile.sheetName
          if (!isSettingChanged) {
            errors.push(
              `ファイル「${file.fileName}」は既に登録されており、変更されていません。ファイルを変更する場合は、ファイル変更ボタンを使用してください。`
            )
          }
        }
      }
    }
  }

  return errors
}

/**
 * 登録時のキー項目空データエラーを収集する
 *
 * キー項目に空（trim後に空文字）の値が存在するとバックエンドのキー項目変換で検証エラーになるため、登録前に検出する。 空判定はバックエンド _validate_key_columns_not_empty と同様に trim
 * 後の空文字で行う。
 *
 * @param {import('./types').FileState[]} changedFiles - 変更があったファイル
 * @param {string} taskKey
 * @returns {Promise<string[]>} エラーメッセージの配列
 */
export const collectPrimaryKeyEmptyValueErrors = async (changedFiles, taskKey) => {
  const errors = []

  for (const file of changedFiles) {
    if (isFileDeleted(file.fileState)) continue

    const pkCol = file.primaryKeyColumn
    if (!pkCol) continue

    const fileStore = createFileStore(file.fileKey, taskKey)
    const uniquePk = await getUniqueValuesForExclusion(file.fileKey, pkCol, fileStore)
    const hasEmpty = [...uniquePk].some((value) => String(value).trim() === '')
    if (hasEmpty) {
      errors.push(`ファイル「${file.fileName}」のキー項目に空データが存在します。`)
    }
  }

  return errors
}

/**
 * 変換表キーから変換表名を解決する（一覧に無い場合はキーをそのまま返す）
 *
 * @param {string} conversionTableKey
 * @param {{ id: string; name: string }[]} [idConversionTables]
 * @returns {string}
 */
const resolveConversionTableName = (conversionTableKey, idConversionTables) => {
  const found = (idConversionTables ?? []).find((t) => t?.id === conversionTableKey)
  return found?.name || conversionTableKey
}

/**
 * 登録時の変換表カバレッジ不足エラーを収集する
 *
 * @param {import('./types').FileState[]} changedFiles
 * @param {string} corporationNumber
 * @param {string} taskKey
 * @param {function(string, string): Promise<any>} fetchConversionValuesFn
 * @param {Record<string, import('./types').FileState> | null | undefined} savedUploadState
 * @param {{ id: string; name: string }[]} [idConversionTables] - 変換表名の解決に使用する
 * @returns {Promise<string[]>}
 */
export const collectConversionTableCoverageErrors = async (
  changedFiles,
  corporationNumber,
  taskKey,
  fetchConversionValuesFn,
  savedUploadState,
  idConversionTables
) => {
  const errors = []

  for (const file of changedFiles) {
    if (isFileDeleted(file.fileState)) continue

    const newTableKey = normalizeConversionTableKey(file.idConversionTable)
    if (newTableKey === null) continue

    const pkCol = file.primaryKeyColumn
    if (!pkCol) continue

    const list = await fetchConversionValueList(fetchConversionValuesFn, corporationNumber, newTableKey)
    if (!list) {
      errors.push(`ファイル「${file.fileName}」の変換表項目を取得できませんでした。`)
      continue
    }

    const useHashSpaceCoverage = file.fileState === FILE_STATE.UPLOADED && file.isUploadedLocally === false

    const savedFile = savedUploadState?.[file.fileKey]
    const oldTableKey = normalizeConversionTableKey(savedFile?.idConversionTable)
    const isConversionTableSwitch =
      useHashSpaceCoverage &&
      oldTableKey !== null &&
      oldTableKey !== newTableKey &&
      savedFile?.fileState === FILE_STATE.UPLOADED

    /** @type {Set<string>} */
    let coverageKeySet
    if (isConversionTableSwitch) {
      const oldList = await fetchConversionValueList(fetchConversionValuesFn, corporationNumber, oldTableKey)
      if (!oldList) {
        errors.push(`ファイル「${file.fileName}」の変換表項目を取得できませんでした。`)
        continue
      }
      const oldPlain = plainMappingFromConversionList(oldList)
      const newPlain = plainMappingFromConversionList(list)
      const switchKeys = await buildSwitchCoverageKeySet(oldPlain, newPlain)
      const forwardKeys = await buildHashSpaceBeforeKeySet(list)
      coverageKeySet = new Set([...switchKeys, ...forwardKeys])
    } else {
      const plainKeys = []
      for (const item of list) {
        const valueBefore = item.value_before
        if (valueBefore === undefined || valueBefore === null) continue
        const trimmed = String(valueBefore).trim()
        if (trimmed === '') continue
        plainKeys.push(trimmed)
      }
      coverageKeySet = new Set(useHashSpaceCoverage ? await sha256HexAll(plainKeys) : plainKeys)
    }

    const fileStore = createFileStore(file.fileKey, taskKey)
    const uniquePk = await getUniqueValuesForExclusion(file.fileKey, pkCol, fileStore)
    let hasUncovered = false
    for (const value of uniquePk) {
      const trimmed = String(value).trim()
      if (trimmed === '') continue
      if (!coverageKeySet.has(trimmed)) {
        hasUncovered = true
        break
      }
    }
    if (hasUncovered) {
      const conversionTableName = resolveConversionTableName(newTableKey, idConversionTables)
      errors.push(
        `ファイル「${file.fileName}」は変換表の項目が不足しているため変換できません。` +
          `　変換表「${conversionTableName}」に不足している従業員IDを追加してください。`
      )
    }
  }

  return errors
}
